import { useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import firebase from "firebase/compat/app";

import { dbFirebase } from "features/firebase";
import { gtagEvent } from "gtag.js";
import { ReportItem, ItemGroup, CategoryJson } from "pages/photo/types";
import { LitterFunction } from "custom/litterFunctions";

import { linkToUploadSuccess } from "routes/upload-success/links";
import useEffectOnMount from "hooks/useEffectOnMount";

import { useUser } from "providers/UserProvider";
import { updateMissionOnPhotoUploaded } from "features/firebase/missions";
import { useMissions } from "providers/MissionsProvider";

import categories from "custom/categories.json";

type ReportItemEntry = {
  fn: LitterFunction;
  item: ReportItem;
};

type HookArgs = {
  imgSrc: string;
  online?: boolean;
  imgLocation: any;
  items: ReportItemEntry[];
};

function transformReportItems(items: ReportItemEntry[]): {
  categories: any[];
  totalCount: number;
} {
  const categoryJson = categories as CategoryJson;
  const transformed: any[] = [];
  let totalCount = 0;

  for (const { item } of items) {
    const label =
      item.category.id && categoryJson[item.category.id]
        ? categoryJson[item.category.id].label
        : item.category.label;

    if (item.groups.length === 0) {
      // No breakdown — single entry
      totalCount += item.totalQuantity;
      transformed.push({
        brand: null,
        brandId: null,
        material: null,
        materialId: null,
        barcode: null,
        number: item.totalQuantity,
        label,
        categoryId: item.category.id,
        leafkey: item.category.id
      });
    } else {
      // Groups exist — one entry per group
      const assignedQuantity = item.groups.reduce(
        (sum: number, g: ItemGroup) => sum + g.quantity,
        0
      );

      for (const group of item.groups) {
        totalCount += group.quantity;
        transformed.push({
          brand: group.brand.label,
          brandId: group.brand.id,
          material: group.material.label,
          materialId: group.material.id,
          barcode: null,
          number: group.quantity,
          label,
          categoryId: item.category.id,
          leafkey: item.category.id
        });
      }

      // Unassigned remainder (no brand/material)
      const remainder = item.totalQuantity - assignedQuantity;
      if (remainder > 0) {
        totalCount += remainder;
        transformed.push({
          brand: null,
          brandId: null,
          material: null,
          materialId: null,
          barcode: null,
          number: remainder,
          label,
          categoryId: item.category.id,
          leafkey: item.category.id
        });
      }
    }
  }

  return { categories: transformed, totalCount };
}

export default function useSubmitReport(args: HookArgs) {
  const [uploadTask, setUploadTask] = useState<any>();
  const [sendingProgress, setSendingProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string>();

  const history = useHistory();
  const missionData = useMissions();
  const { user } = useUser();
  const { t } = useTranslation();

  const missionIds = user?.missions || [];
  const uploaderId = user?.id || "";
  const userDisplayName = user?.displayName || "";

  const submitReport = async () => {
    try {
      if (!args.online) {
        throw new Error("record_litter_offline_text");
      }

      gtagEvent("Upload", "Photo" as any);

      const { categories: transformedItems, totalCount } = transformReportItems(
        args.items
      );

      // FOR DEBUGGING — log the transformed items that will be sent to Firestore
      // console.log("Transformed items for upload", {
      //   transformedItems,
      //   totalCount
      // });

      const dataToSend = {
        ...args.imgLocation,
        pieces: totalCount,
        categories: transformedItems,
        missions: missionIds
      };

      // FOR DEBUGGING — log the full data payload that will be sent to Firestore
      // console.log("Data to send for report upload", dataToSend);

      const base64 = args.imgSrc.split(",")[1];

      if (!base64) {
        console.warn(
          "[useSubmitReport] Could not derive base64 payload from imgSrc.",
          {
            hasImgSrc: !!args.imgSrc,
            imgSrcLength: args.imgSrc.length,
            imgSrcPreview: args.imgSrc.slice(0, 80)
          }
        );
      }

      const photoRef = await dbFirebase.saveMetadata(dataToSend);

      try {
        await updateMissionOnPhotoUploaded(
          firebase.firestore(),
          uploaderId,
          totalCount,
          missionIds,
          userDisplayName
        );
      } catch (error) {
        console.error(error);
      }

      const uploadTask = dbFirebase.savePhoto(photoRef.id, base64);
      setUploadTask(uploadTask);

      uploadTask.on(
        firebase.storage.TaskEvent.STATE_CHANGED,
        (snapshot: any) => {
          const progress = Math.ceil(
            (snapshot.bytesTransferred / snapshot.totalBytes) * 98 + 1
          );
          setSendingProgress(progress);

          switch (snapshot.state) {
            case firebase.storage.TaskState.PAUSED: // or 'paused'
              console.log("Upload is paused");
              break;
            case firebase.storage.TaskState.RUNNING: // or 'running'
              console.log("Upload is running");
              break;
            default:
              console.log(snapshot.state);
          }
        }
      );

      await uploadTask;
      console.log(
        `Successfully uploaded report ${photoRef.id} with ${totalCount} pieces. Uploader: ${uploaderId}.`
      );
      await missionData?.refresh();
      history.push(linkToUploadSuccess(missionIds.length > 0));
    } catch (err) {
      setErrorMessage(t(err instanceof Error ? err.message : ""));
    }
  };

  const cancelUpload = () => {
    uploadTask && uploadTask.cancel();
    history.goBack();
  };

  useEffectOnMount(submitReport);

  return {
    sendingProgress,
    cancelUpload,
    errorMessage,
    closeErrorDialog: () => {
      cancelUpload();
      setErrorMessage(undefined);
    }
  };
}
